import React, { useEffect, useState } from 'react';
import palette from './palette';

interface ExampleFormat {
  format: string;
  purpose?: string;
  summary: React.ReactNode;
  files: { name: string; href: string }[];
  rules?: React.ReactNode;
}

interface ExampleFilesGuideProps {
  formats: ExampleFormat[];
  intro?: React.ReactNode;
}

// Scoped styles let the native controls handle hover, focus and disclosure state.
const styles = `
  /* A tinted strip with blue text, so it reads as help to open rather than
     as another white input. */
  .example-files-guide {
    margin: 0 0 10px;
    overflow: hidden;
    border: 1px solid ${palette.border};
    border-radius: 4px;
    background: ${palette.panelBackground};
    font-size: 13px;
    line-height: 1.43;
  }
  .example-files-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    box-sizing: border-box;
    width: 100%;
    height: 34px;
    padding: 0 12px;
    margin: 0;
    border: 0;
    background: transparent;
    color: ${palette.brandBlue};
    font: inherit;
    font-size: 13px;
    text-align: left;
    cursor: pointer;
  }
  .example-files-toggle:hover { background: ${palette.tintBackground}; }
  .example-files-toggle:hover strong { text-decoration: underline; }
  /* Inset, so the strip's rounded clipping cannot hide it. */
  .example-files-toggle:focus-visible {
    outline: 2px solid ${palette.brandBlue};
    outline-offset: -2px;
  }
  .example-files-folder { width: 14px; flex-shrink: 0; color: ${palette.accent}; }
  .example-files-toggle strong { white-space: nowrap; font-weight: 700; }
  .example-files-phrase { color: ${palette.muted}; font-weight: 300; }
  .example-files-chevron {
    margin-left: auto;
    flex-shrink: 0;
    color: ${palette.accent};
    font-size: 12px;
  }
  .example-files-content {
    padding: 10px 12px;
    border-top: 1px solid ${palette.border};
  }
  .example-files-intro { margin: 0 0 8px; color: ${palette.muted}; }
  .example-files-format {
    display: grid;
    grid-template-columns: 74px minmax(0, 1fr);
    column-gap: 10px;
    padding: 8px 0;
  }
  .example-files-format + .example-files-format {
    border-top: 1px solid ${palette.border};
  }
  .example-files-badge {
    justify-self: start;
    align-self: start;
    margin-top: 1px;
    padding: 1px 6px;
    border-radius: 3px;
    background: ${palette.badgeBackground};
    color: ${palette.accentDeep};
    font: 700 10.5px/1.6 Menlo, Monaco, 'Courier New', monospace;
    letter-spacing: .02em;
  }
  .example-files-purpose { color: ${palette.heading}; font-weight: 700; }
  .example-files-summary { color: ${palette.body}; margin: 1px 0 6px; }
  .example-files-downloads {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .example-files-download {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 3px 9px;
    background: ${palette.cardBackground};
    border: 1px solid ${palette.borderSoft};
    border-radius: 4px;
    color: ${palette.text};
    font-size: 12px;
    text-decoration: none;
  }
  .example-files-download:hover {
    border-color: ${palette.accent};
    background: ${palette.hoverBackground};
    color: ${palette.accentDeep};
    text-decoration: none;
  }
  .example-files-download i { color: ${palette.accent}; }
  /* Keep Bootstrap's code colors out of the guide. */
  .example-files-guide code {
    font: 12px Menlo, Monaco, 'Courier New', monospace;
    color: inherit;
    background: none;
    padding: 0;
  }
  .example-files-rules { margin-top: 6px; }
  .example-files-rules summary {
    cursor: pointer;
    color: ${palette.brandBlue};
    font-size: 12px;
  }
  .example-files-rules ul { margin: 6px 0 0; padding-left: 18px; color: ${palette.body}; }
  .example-files-rules li { margin: 2px 0; }
`;

export default function ExampleFilesGuide({
  formats,
  intro,
}: ExampleFilesGuideProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(formats.length === 1);

  useEffect(() => {
    // Also reveal the guide when an analysis change narrows it to one format.
    if (formats.length === 1) setIsOpen(true);
  }, [formats.length]);

  const names = formats.map(row => row.format);
  const formatPhrase =
    names.length > 1
      ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
      : names[0];

  return (
    <div className="example-files-guide">
      <style>{styles}</style>
      <button
        type="button"
        className="example-files-toggle"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
      >
        <i
          className={`fa fa-folder${
            isOpen ? '-open' : ''
          }-o example-files-folder`}
          aria-hidden="true"
        />
        <strong>Example files</strong>
        <span className="example-files-phrase">
          in {formatPhrase} {names.length === 1 ? 'format' : 'formats'}
        </span>
        <i
          className={`fa fa-chevron-${
            isOpen ? 'up' : 'down'
          } example-files-chevron`}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div className="example-files-content">
          {intro && <p className="example-files-intro">{intro}</p>}
          {formats.map(({ format, purpose, summary, files, rules }) => (
            <section
              className="example-files-format"
              aria-label={`${format} example files`}
              key={format}
            >
              <span className="example-files-badge">{format}</span>
              <div>
                {purpose && (
                  <div className="example-files-purpose">{purpose}</div>
                )}
                <div className="example-files-summary">{summary}</div>
                <ul className="example-files-downloads">
                  {files.map(({ name, href }) => (
                    <li key={href}>
                      {/* The examples are on another origin, where browsers
                          ignore `download` and show text inline; a new tab
                          keeps the form and its inputs in place. */}
                      <a
                        className="example-files-download"
                        href={href}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fa fa-download" aria-hidden="true" />
                        <code>{name}</code>
                      </a>
                    </li>
                  ))}
                </ul>
                {rules && (
                  <details className="example-files-rules">
                    <summary>Format rules</summary>
                    {rules}
                  </details>
                )}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
